import React, { useEffect, useRef, useState } from "react";
import { Grid, Card, CardMedia, Typography, Box } from "@mui/material";
import { useContent } from "../context/ContentContext";

export default function ServicesPage() {
  const content = useContent();
  const services = content.getServices();

  // Track which cards are visible
  const [visibleCards, setVisibleCards] = useState([]);
  const cardRefs = useRef([]);

  useEffect(() => {
    if (!cardRefs.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.dataset.index);
            setVisibleCards((prev) => {
              if (!prev.includes(index)) return [...prev, index];
              return prev;
            });
            observer.unobserve(entry.target); // animate only once
          }
        });
      },
      { threshold: 0.2 } // trigger when 20% of the card is visible
    );

    cardRefs.current.forEach((ref) => ref && observer.observe(ref));

    return () => observer.disconnect();
  }, [services]);

  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      {services.map((s, i) => (
        <Grid item xs={12} md={6} key={i}>
          <Card
            ref={(el) => (cardRefs.current[i] = el)}
            data-index={i}
            sx={{
              position: "relative",
              overflow: "hidden",
              bgcolor: "#f7f7fa",
              boxShadow: 2,
              borderRadius: 3,
              m: 1,
              border: "4px solid #d1d5db",
              height: 300,
              display: "flex",
              flexDirection: "column",
              opacity: visibleCards.includes(i) ? 1 : 0,
              transform: visibleCards.includes(i) ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.6s ease, transform 0.6s ease",
              '&:hover .service-overlay': {
                opacity: 1,
              },
            }}
          >
            {s.image && (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <CardMedia
                  component="img"
                  image={s.image}
                  alt={s.title}
                  sx={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}
            {/* Overlay text */}
            <Box
              className="service-overlay"
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
                bgcolor: "rgba(30,30,40,0.8)",
                color: "#fff",
                px: 3,
                py: 1.2,
                backdropFilter: "blur(1px)",
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                opacity: 1,
                transition: "opacity 0.3s, padding 0.3s",
                zIndex: 4,
                display: "flex",
                flexDirection: "column",
                pointerEvents: "none",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: 0.5, mb: 0.5 }}>
                {s.title}
              </Typography>
              <Box
                className="service-desc"
                sx={{
                  maxHeight: 0,
                  overflow: "hidden",
                  opacity: 0,
                  transition: "opacity 0.3s, max-height 0.3s",
                }}
              >
                <Typography variant="body2" sx={{ mb: 0.5, lineHeight: 1.7 }}>
                  {s.description}
                </Typography>
              </Box>
            </Box>
            <style>{`
              .MuiCard-root:hover .service-overlay .service-desc {
                max-height: 200px;
                opacity: 1;
                pointer-events: auto;
              }
            `}</style>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
